/**
 * FmStandMap — the unified DS stand-map component.
 *
 * ONE component that draws forest stands on a Leaflet surface with UNIFORM
 * stand-polygon styling, parameterised by options. It is the single home for
 * stand-drawing across the app (sale-scope, ops-planning, measurement,
 * stand-detail, export/PDF) so the many bespoke per-map stand layers collapse to
 * one. Built ON the existing DS map primitives.
 */
import React from "react";
import type { FmStandMapProps } from "./FmStandMap.types";
import { FmStandMapBody, FmStandMapFillHost } from "./FmStandMap.parts";
import { useFmStandMapModel } from "./useFmStandMapModel";
import { FmEmpty, FmSpinner } from "@/components/ui/FmFeedback";
import "@/components/ui/FmStandMap.css";

export type {
  FmStandMapProps,
  StandBasemap,
  StandColoringStrategy,
  StandInteractionMode,
  StandStyleContext,
  StandViewportPolicy,
} from "./FmStandMap.types";

function resolvedMapOptions(props: FmStandMapProps): FmStandMapProps["mapOptions"] {
  if (props.viewportPolicy !== "embedded-controlled") return props.mapOptions;
  return {
    ...props.mapOptions,
    scrollWheelZoom: false,
    doubleClickZoom: false,
    dragging: false,
    zoomControl: false,
    // Property review maps are often long and narrow. Whole integer zoom
    // levels can leave half the canvas unused; quarter steps keep the complete
    // boundary visible while making the operation colours legible.
    zoomSnap: props.mapOptions?.zoomSnap ?? 0.25,
  };
}

export const FmStandMap: React.FC<FmStandMapProps> = (props) => {
  const model = useFmStandMapModel(props);
  return (
    <FmStandMapFillHost fill={props.fill ?? false} fillHostRef={model.fillHostRef}>
      {model.loading ? (
        <div className="fm-stand-map__state" style={{ minHeight: model.resolvedHeight }}>
          <FmSpinner tip={props.loadingLabel} />
        </div>
      ) : model.error || !model.usable ? (
        <div className="fm-stand-map__state" style={{ minHeight: model.resolvedHeight }}>
          <FmEmpty description={props.unavailableLabel} />
        </div>
      ) : (
        <FmStandMapBody
        geojson={model.geojson}
        bbox={model.bbox}
        borderRadius={props.borderRadius ?? 0}
        testId={props.testId}
        mapOptions={resolvedMapOptions(props)}
        viewportPolicy={props.viewportPolicy ?? "standard"}
        viewportIdentity={props.propertyId}
        fitFeatureIds={props.fitFeatureIds}
        defaultCenter={props.defaultCenter}
        defaultZoom={props.defaultZoom}
        basemap={props.basemap ?? "maastokartta"}
        basemapTone={props.basemapTone ?? "standard"}
        onRasterUnavailableChange={props.onRasterUnavailableChange}
        standPane={props.standPane}
        mapChildren={props.mapChildren}
        overlays={props.overlays}
        legendPortalTarget={props.legendPortalTarget}
        controlsPortalTarget={props.controlsPortalTarget}
        resolvedHeight={model.resolvedHeight}
        layerKey={model.layerKey}
        layerStyle={model.layerStyle}
        onEachFeature={model.onEachFeature}
        />
      )}
    </FmStandMapFillHost>
  );
};

export default FmStandMap;
